import React, { useMemo, useState } from "react";
import { CircularProgress, makeStyles, TextField } from "@material-ui/core";
import { useGetCategoryIdToHumanReadableName } from "../../../common/Hooks/useGetCategoryIdToHumanReadableName";
import { useEnumStrings } from "../../../enumStrings";
import IdaChatbotIcon from "../../../../images/ida-chatbot-icon.png";
import { CmpPopup, cmpPopupButton, CMP_POPUP_COLORS } from "../../../common/CmpDesignPopup";
import { CmpProfileIssue, getIssuePopupHeading } from "./cmpProfileSections";

// Claude Design "CMP Accelerator Member Dashboard" (Oct 15 2026), screen
// "Manage The Coffee House Profile". Chamber Member Pro members only; the
// Chamber Perks App only (CPA-only) page keeps its own layout. Texts are the
// design text verbatim.

export const GOLD = "#E1A731";
const GOLD_DARK = "#C6912A";
const TEXT = "#000000";
const BLACK = "#0A0A0A";
const BORDER = "#9E9E9E";
const GRAY_600 = "#727272";
const GREEN = "#10B981";
const RED = "#ED1C24";

export const CMP_PROFILE_TEXT = {
    subtitle: "Update your company information that will show in our Canada-wide Chamber Perks App™ and website directories.",
    intro1: "Update your company profile information below. The details you provide will appear in our website directory and in the Chamber Perks App™ global directory helping increase your companies visibility.",
    intro2: "If there is any information you do not want displayed publicly, simply leave that field blank.",
    descriptionHelp: "Update your company description which will be seen in our website directory and in the Chamber Perks App™ global directory. Use our AI Assistant (IDA) to help generate your company description. Simply enter content or your website URL, then ask IDA to generate.",
    categoriesLabel: "Website and Chamber Perks App™ Directory Categories",
    categoriesHelp: "Select 1 to 2 categories from the drop-down that best represent your company. These category filters will be used in the website directory and Chamber Perks App™ global directory for users to search your company.",
    addressHelp: "Update your company address as it will appear on both our website member directory and the Chamber Perks App™ global member directory",
    hideAddress: "Check this box if you don’t want the address to appear in any directories. City and province fields will only show.",
    socialHelp: "Update your company’s social media links to display on both our website member directory and the Chamber Perks App™ global member directory. Copy and paste your URL from each social media account below.",
    primaryHelp: "Update the Primary Contact/Voting Member details below if required. As the company primary contact, you can grant access to designated employees to access your member dashboard.",
    notInDirectory: "This contact information will NOT appear on our website directory or in the Chamber Perks App™ global directories, unless it is entered as the main company contact.",
    bannersAllowOff: "Featured Member Banners allow you to add image and video banners to your company profile and perks.",
    bannersAllowOn: "Featured Member Banners allow you to add interchangeable image and video banners to your company profile and up to 5 perks.",
};

/** Design card, pill, label and input styles. Also passed to the company contact cards. */
export const useCmpProfileStyles = makeStyles(() => ({
    root: { background: "#F7F7F8", minHeight: "100vh", paddingBottom: 48, overflowX: "hidden" as const, fontFamily: "'DM Sans', sans-serif" },
    header: { background: GOLD, padding: "28px 40px 100px", borderRadius: "0 0 18px 18px" },
    headerTitle: { fontSize: 28, fontWeight: 600, color: "#FFFFFF", margin: "0 0 8px", fontFamily: "'DM Sans', sans-serif" },
    headerSubtitle: { fontSize: 15, color: "#FFFFFF", marginBottom: 18 },
    body: {
        background: "#FFFFFF", borderRadius: 18, margin: "-72px 28px 0", padding: "16px 32px 40px",
        display: "flex", flexDirection: "column" as const, gap: 48,
        "@media (max-width: 768px)": { margin: "-72px 8px 0", padding: "16px 14px 32px" },
    },
    formSection: { position: "relative" as const, border: "1px solid #CFCFCF", borderRadius: 12, padding: "40px 28px 28px", "&:first-child": { marginTop: 28 } },
    sectionHeader: { position: "absolute" as const, top: -17, left: 16, background: GOLD, borderRadius: 999, padding: "8px 20px" },
    sectionHeaderBlack: { position: "absolute" as const, top: -17, left: 16, background: BLACK, borderRadius: 999, padding: "8px 20px" },
    sectionHeaderGreen: { position: "absolute" as const, top: -17, left: 16, background: GREEN, borderRadius: 999, padding: "8px 20px" },
    sectionTitle: { margin: 0, color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 },
    sectionHelperText: { fontSize: 14, color: TEXT, lineHeight: 1.55, margin: "0 0 12px", fontFamily: "'DM Sans', sans-serif" },
    fieldLabel: { fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" },
    requiredAsterisk: { color: RED },
    companyInput: {
        width: "100%",
        "& .MuiOutlinedInput-root": { borderRadius: 8, background: "#FFFFFF", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: TEXT },
        "& .MuiOutlinedInput-input": { padding: "11px 12px", height: "auto" },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
        "& .MuiFormHelperText-root": { marginLeft: 0, fontFamily: "'DM Sans', sans-serif" },
    },
    formRow: {
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px 40px", marginBottom: 24,
        "@media (max-width: 768px)": { gridTemplateColumns: "1fr" },
    },
    stack: { display: "flex", flexDirection: "column" as const, gap: 24 },
    field: { display: "flex", flexDirection: "column" as const, gap: 8, minWidth: 0 },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px 40px", "@media (max-width: 768px)": { gridTemplateColumns: "1fr" } },
    grid4: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 76px", gap: "24px 32px", "@media (max-width: 900px)": { gridTemplateColumns: "1fr 1fr" } },
    label: { fontSize: 14, fontWeight: 600, color: TEXT, fontFamily: "'DM Sans', sans-serif" },
    help: { fontSize: 14, color: TEXT, lineHeight: 1.55 },
    generate: {
        color: "#FFFFFF", border: "none", borderRadius: 999, padding: "6px 18px", fontFamily: "'DM Sans', sans-serif",
        fontSize: 12, fontWeight: 600, cursor: "pointer", backgroundColor: GREEN,
    },
    comingSoon: { color: "#FFFFFF", borderRadius: 999, padding: "6px 18px", fontSize: 12, fontWeight: 600, backgroundColor: BORDER },
    bannerGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 24, alignItems: "stretch", maxWidth: 900, margin: "0 auto", width: "100%",
        "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, minmax(0,1fr))" } },
    bannerSlot: { display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 10, minWidth: 0, width: "100%" },
    bannerButton: { border: "none", borderRadius: 8, padding: "12px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", color: "#FFFFFF" },
    // Card footer (D2): Reset, Save Changes, Last Saved.
    footerReset: {
        background: "#FFFFFF", color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "11px 28px", fontFamily: "'DM Sans', sans-serif",
        fontSize: 14, fontWeight: 600, cursor: "pointer", "&:hover": { background: "#F4F4F5" }, "&:disabled": { opacity: 0.6, cursor: "default" },
    },
    footerSave: {
        background: GOLD, color: "#FFFFFF", border: "none", borderRadius: 8, padding: "12px 28px", fontFamily: "'DM Sans', sans-serif",
        fontSize: 14, fontWeight: 600, cursor: "pointer", "&:hover": { background: GOLD_DARK }, "&:disabled": { opacity: 0.6, cursor: "default" },
    },
    update: {
        background: GOLD, color: "#FFFFFF", border: "none", borderRadius: 8, padding: "13px 64px", fontFamily: "'DM Sans', sans-serif",
        fontSize: 14, fontWeight: 600, letterSpacing: "0.04em", cursor: "pointer", "&:hover": { background: GOLD_DARK }, "&:disabled": { opacity: 0.6, cursor: "default" },
    },
}));

type CmpClasses = ReturnType<typeof useCmpProfileStyles>;

export const CmpCard = ({ title, tone = "gold", classes, children, testId, section, footer }: {
    title: string; tone?: "gold" | "black" | "green"; classes: CmpClasses; children: React.ReactNode; testId?: string;
    /** Design data-section id (D1). */
    section?: string;
    footer?: React.ReactNode;
}) => (
    <div className={classes.formSection} data-testid={testId} data-section={section}>
        <div className={tone === "black" ? classes.sectionHeaderBlack : tone === "green" ? classes.sectionHeaderGreen : classes.sectionHeader}>
            <p className={classes.sectionTitle}>{title}</p>
        </div>
        {children}
        {footer}
    </div>
);

/** D2 card footer: Reset, Save Changes and "Last Saved <date>" (nothing until the card has a saved time). */
export const CmpCardFooter = ({ classes, onReset, onSave, lastSaved, busy, testId }: {
    classes: CmpClasses; onReset: () => void; onSave: () => void; lastSaved?: string | null; busy?: boolean; testId?: string;
}) => (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, marginTop: 28, paddingTop: 20, borderTop: "1px solid #ECECEE" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button type="button" className={classes.footerReset} onClick={onReset} disabled={busy}>Reset</button>
            <button type="button" className={classes.footerSave} onClick={onSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</button>
        </div>
        {lastSaved ? <div style={{ fontSize: 13, color: GRAY_600 }}>{lastSaved}</div> : null}
    </div>
);

export type CmpProfileResult =
    | { ok: true; title: string; message: string }
    | { ok: false; issues: CmpProfileIssue[] };

/** D17/D18 result popups: CHANGES SAVED / PROFILE UPDATED with OK, or the list of fields to fix. */
export const CmpProfileResultPopup = ({ result, onClose }: { result: CmpProfileResult; onClose: () => void }) => {
    if (result.ok) {
        return (
            <CmpPopup label={result.title} tone="success" title={result.title} onClose={onClose}>
                <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>{result.message}</div>
                <button type="button" onClick={onClose} style={{ ...cmpPopupButton(CMP_POPUP_COLORS.gold), marginTop: 24, minWidth: 160, padding: "12px 24px" }}>OK</button>
            </CmpPopup>
        );
    }
    const heading = getIssuePopupHeading(result.issues);
    return (
        <CmpPopup label={heading.title} tone="warning" title={heading.title} onClose={onClose}>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>{heading.intro}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14, width: "100%", textAlign: "left", background: "#FDECEC",
                borderRadius: 8, padding: "12px 16px", boxSizing: "border-box" }}>
                {result.issues.map((issue, index) => (
                    <div key={`${issue.field}-${index}`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#B4141B", lineHeight: 1.45 }}>{issue.field}</div>
                        <div style={{ fontSize: 13, color: "#B4141B", lineHeight: 1.45 }}>{issue.why}</div>
                    </div>
                ))}
            </div>
        </CmpPopup>
    );
};

export const CmpField = ({ label, required, classes, children }: {
    label: string; required?: boolean; classes: CmpClasses; children: React.ReactNode;
}) => (
    <div className={classes.field}>
        <label className={classes.label}>{label}{required && <span style={{ color: RED }}>*</span>}</label>
        {children}
    </div>
);

const UploadIcon = ({ color }: { color: string }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15.5V4.5" /><path d="M6.8 8.7l5.2-5.2 5.2 5.2" /><path d="M4 15.5v2.7a2.3 2.3 0 002.3 2.3h11.4a2.3 2.3 0 002.3-2.3v-2.7" />
    </svg>
);

/** Design logo box: dashed "Click Upload Logo", or the logo with Update Logo / Delete Logo. */
export const CmpLogoPicker = ({ logo, onFile, onDelete }: {
    logo: string; onFile: (event: React.ChangeEvent<HTMLInputElement>) => void; onDelete: (event: { preventDefault: () => void }) => void;
}) => {
    const id = useMemo(() => `cmp-logo-${Math.random().toString(36).slice(2)}`, []);
    const pill = (background: string): React.CSSProperties => ({ display: "inline-flex", alignItems: "center", gap: 10, background, color: "#FFFFFF", border: "none",
        borderRadius: 999, padding: "12px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600, cursor: "pointer" });
    return (
        <>
            <input id={id} type="file" accept="image/png,image/jpeg" style={{ display: "none" }} data-testid="cmp-logo-input"
                onChange={(event) => { onFile(event); event.target.value = ""; }} />
            {!logo ? (
                <label htmlFor={id} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: `1px dashed ${BORDER}`, borderRadius: 8,
                    minHeight: 44, flex: 1, padding: "7px 14px", boxSizing: "border-box", marginTop: 4, cursor: "pointer" }}>
                    <UploadIcon color={GRAY_600} />
                    <span style={{ fontSize: 14, fontWeight: 400, color: GRAY_600 }}>Click Upload Logo</span>
                </label>
            ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 4, flex: 1, flexWrap: "wrap" }}>
                    <div style={{ width: 150, height: 150, border: `1.5px solid ${GRAY_600}`, borderRadius: 16, background: "#FFFFFF", flexShrink: 0, boxSizing: "border-box" }}>
                        <div style={{ width: "100%", height: "100%", backgroundImage: `url("${logo}")`, backgroundSize: "contain", backgroundRepeat: "no-repeat",
                            backgroundPosition: "center", borderRadius: 14 }} role="img" aria-label="Company logo" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <label htmlFor={id} style={pill("#6B6B6B")}>Update Logo</label>
                        <button type="button" onClick={onDelete} style={pill(RED)}>Delete Logo</button>
                    </div>
                </div>
            )}
        </>
    );
};

/** Design category drop-down: search, check boxes, 1 to 2 categories, chips with a red remove. */
export const CmpCategoryPicker = ({ selected, onChange, error }: {
    selected: string[]; onChange: (next: string[]) => void; error?: string;
}) => {
    const { categoryIdToHumanReadableName, allCategories } = useGetCategoryIdToHumanReadableName();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const options = useMemo(() => (allCategories || []).slice()
        .sort((a: { name: string }, b: { name: string }) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .filter((category: { name: string }) => category.name.toLowerCase().includes(search.trim().toLowerCase())), [allCategories, search]);
    const toggle = (id: string) => {
        if (selected.includes(id)) onChange(selected.filter((value) => value !== id));
        else if (selected.length < 2) onChange([...selected, id]);
    };
    const names = (categoryIdToHumanReadableName || {}) as Record<string, string>;
    return (
        <div style={{ position: "relative" }}>
            <button type="button" onClick={() => setOpen((value) => !value)} aria-haspopup="listbox" aria-expanded={open} data-testid="cmp-category-toggle"
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#FFFFFF",
                    border: `1px solid ${error ? RED : BORDER}`, borderRadius: 8, padding: "7px 14px", minHeight: 44, fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14, color: selected.length ? TEXT : GRAY_600, cursor: "pointer", boxSizing: "border-box" }}>
                {selected.length ? (
                    <span style={{ display: "flex", flexWrap: "wrap", gap: 6, flex: 1, justifyContent: "flex-start" }}>
                        {selected.map((id) => (
                            <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${BORDER}`, borderRadius: 999, padding: "3px 10px",
                                fontSize: 13, color: TEXT, background: "#F7F7F8" }}>
                                {names[id] || id}
                                <span role="button" aria-label={`Remove ${names[id] || id}`} onClick={(event) => { event.stopPropagation(); toggle(id); }}
                                    style={{ display: "inline-flex", width: 14, height: 14, borderRadius: "50%", background: RED, color: "#FFFFFF", fontSize: 10,
                                        alignItems: "center", justifyContent: "center", cursor: "pointer" }}>✕</span>
                            </span>
                        ))}
                    </span>
                ) : <span>Select Categories</span>}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 59 }} />
                    <div role="listbox" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#FFFFFF", border: `1px solid ${BORDER}`,
                        borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,.12)", zIndex: 60, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <div style={{ padding: "10px 12px", borderBottom: "1px solid #ECECEC" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GRAY_600} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
                                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search Categories..."
                                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, border: "none", outline: "none", flex: 1, color: TEXT, background: "transparent" }} />
                            </div>
                        </div>
                        <div style={{ maxHeight: 240, overflowY: "auto", padding: "6px 0" }}>
                            {options.map((category: { id: string; name: string }) => {
                                const checked = selected.includes(category.id);
                                const blocked = !checked && selected.length >= 2;
                                return (
                                    <div key={category.id} role="option" aria-selected={checked} onClick={() => !blocked && toggle(category.id)}
                                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", cursor: blocked ? "not-allowed" : "pointer", opacity: blocked ? 0.45 : 1 }}>
                                        <span style={{ width: 17, height: 17, borderRadius: 4, border: `1.5px solid ${checked ? GREEN : BORDER}`, background: "#FFFFFF",
                                            flexShrink: 0, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", color: GREEN, fontSize: 12 }}>
                                            {checked ? "✓" : ""}
                                        </span>
                                        <span style={{ fontSize: 14, color: TEXT }}>{category.name}</span>
                                    </div>
                                );
                            })}
                            {!options.length && <div style={{ padding: "12px 14px", fontSize: 13, color: GRAY_600 }}>No categories match your search.</div>}
                        </div>
                    </div>
                </>
            )}
            {error && <div style={{ color: RED, fontSize: 12, marginTop: 4 }}>{error}</div>}
        </div>
    );
};

/** Design province select (stores the province code, like the existing picker). */
export const CmpProvinceSelect = ({ value, onChange, error }: { value: string; onChange: (code: string) => void; error?: string }) => {
    const enumStrings = useEnumStrings();
    const options = enumStrings.provinceDropDownSelect.dropdownOptions as Array<{ code: string; full: string }>;
    // Older records store the full name ("Manitoba") or a value outside the list:
    // show the matching option, and keep an unknown value as its own option so it is never lost.
    const raw = (value || "").trim();
    const match = options.find((option) => option.code === raw || option.full.toLowerCase() === raw.toLowerCase());
    const selected = match ? match.code : raw;
    return (
        <div style={{ position: "relative" }}>
            <select value={selected} onChange={(event) => onChange(event.target.value)} aria-label="Province/State"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, border: `1px solid ${error ? RED : BORDER}`, borderRadius: 8, padding: "11px 12px",
                    color: value ? TEXT : GRAY_600, outline: "none", width: "100%", boxSizing: "border-box", appearance: "none", background: "#FFFFFF" }}>
                <option value="" disabled>Select Province/State</option>
                {raw && !match && <option value={raw}>{raw}</option>}
                {options.map((option) => <option key={option.code} value={option.code}>{option.full}</option>)}
            </select>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLACK} strokeWidth="2" strokeLinecap="round"
                style={{ position: "absolute", right: 12, top: "50%", marginTop: -7, pointerEvents: "none" }}><path d="M6 9l6 6 6-6" /></svg>
            {error && <div style={{ color: RED, fontSize: 12, marginTop: 4 }}>{error}</div>}
        </div>
    );
};

export const CmpIdaGenerate = ({ count, limit, enabled, label, onGenerate, classes }: {
    count: number; limit: number; enabled: boolean; label: string; onGenerate: () => void; classes: CmpClasses;
}) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 14, color: TEXT }}>{count}/{limit} characters</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <img src={IdaChatbotIcon} alt="IDA" style={{ height: 30, width: "auto", flexShrink: 0 }} />
            {enabled
                ? <button type="button" className={classes.generate} onClick={onGenerate}>{label}</button>
                : <span className={classes.comingSoon}>Coming Soon</span>}
        </span>
    </div>
);

type Banner = { image: string | null; videoUrl: string | null; redirectUrl: string | null };

/** Design Featured Member Profile Banners card, activated and not activated. */
export const CmpProfileBanners = ({ active, banners, classes, onOpen, onDelete, onMove, onPreview, onContactChamber, footer }: {
    active: boolean; banners: Banner[]; classes: CmpClasses; onOpen: (slot: number) => void; onDelete: (index: number) => void;
    onMove: (index: number, direction: "left" | "right") => void; onPreview: () => void; onContactChamber: () => void; footer?: React.ReactNode;
}) => {
    const arrow = (enabled: boolean, onClick: () => void, glyph: string, label: string) => (
        <button type="button" aria-label={label} disabled={!enabled} onClick={onClick}
            style={{ display: "inline-flex", width: 26, height: 24, borderRadius: 6, alignItems: "center", justifyContent: "center", border: "none",
                background: enabled ? GOLD : "#C6C6C6", color: "#FFFFFF", fontSize: 12, fontWeight: 600, cursor: enabled ? "pointer" : "default" }}>{glyph}</button>
    );
    return (
        <CmpCard title={active ? "Featured Member Profile Banners Activated" : "Featured Member Profile Banners: NOT ACTIVATED"}
            tone={active ? "green" : "black"} classes={classes} testId="cmp-profile-banners" section="featured" footer={footer}>
            {active ? (
                <>
                    <p style={{ fontSize: 14, color: TEXT, margin: "0 0 10px", lineHeight: 1.55, textAlign: "center" }}>{CMP_PROFILE_TEXT.bannersAllowOn}</p>
                    <p style={{ fontSize: 14, color: TEXT, textAlign: "center", margin: "0 0 10px", lineHeight: 1.55 }}><b>As a Featured Member</b>, you can now upload up to 4 image or video banners to promote your company profile on our Chamber Perks App™ and website directories.</p>
                    <p style={{ fontSize: 14, color: TEXT, textAlign: "center", margin: "0 0 24px" }}>For best quality, we recommend an aspect ratio of <b>16:9 with a minimum size of 1280 × 720.</b></p>
                </>
            ) : (
                <>
                    <p style={{ fontSize: 14, color: TEXT, margin: "0 0 14px", lineHeight: 1.55, textAlign: "center" }}>{CMP_PROFILE_TEXT.bannersAllowOff}</p>
                    <p style={{ fontSize: 14, color: TEXT, textAlign: "center", margin: "0 0 28px", lineHeight: 1.55 }}>
                        You have <b>not upgraded</b> to a featured member and therefore can not add image or video banners. If you would like to learn more,{" "}
                        <a role="button" tabIndex={0} onClick={onContactChamber} onKeyDown={(event) => { if (event.key === "Enter") onContactChamber(); }}
                            style={{ color: GOLD, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>click here</a> to contact your chamber.
                    </p>
                </>
            )}
            <div className={classes.bannerGrid}>
                {[0, 1, 2, 3].map((index) => {
                    const banner = banners[index] || { image: null, videoUrl: null, redirectUrl: null };
                    const filled = !!(banner.image || banner.videoUrl);
                    return (
                        <div key={index} className={classes.bannerSlot}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: active ? GOLD_DARK : GRAY_600, whiteSpace: "nowrap" }}>Banner {index + 1}</div>
                            {!active ? (
                                <div style={{ width: "100%", background: BORDER, borderRadius: 10, aspectRatio: "16 / 9", display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", gap: 8, minWidth: 0, padding: 8, boxSizing: "border-box" }}>
                                    <UploadIcon color="#FFFFFF" />
                                    <span style={{ fontSize: 14, fontWeight: 600, color: "#FFFFFF", textAlign: "center" }}>Upload Banner</span>
                                </div>
                            ) : filled ? (
                                <div style={{ width: "100%", flex: 1, border: `2px solid ${GOLD}`, borderRadius: 12, padding: 8, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 8 }}>
                                    {banner.image ? (
                                        <div style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: 8, backgroundImage: `url("${banner.image}")`, backgroundSize: "cover", backgroundPosition: "center" }}
                                            role="img" aria-label={`Banner ${index + 1}`} />
                                    ) : (
                                        <div style={{ width: "100%", aspectRatio: "16 / 9", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                                            background: "#FFFFFF", border: `1.5px solid ${GOLD}`, borderRadius: 8, boxSizing: "border-box" }}>
                                            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.6"><circle cx="12" cy="12" r="10" /><path d="M10 8l6 4-6 4z" strokeLinejoin="round" /></svg>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: GOLD }}>Watch Video</span>
                                        </div>
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <button type="button" aria-label={`Edit banner ${index + 1}`} onClick={() => onOpen(index + 1)}
                                                style={{ border: `1px solid ${BORDER}`, background: "#FFFFFF", borderRadius: 6, padding: "5px 8px", cursor: "pointer" }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLACK} strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" /></svg>
                                            </button>
                                            <button type="button" aria-label={`Delete banner ${index + 1}`} onClick={() => onDelete(index)}
                                                style={{ border: `1px solid ${RED}`, background: RED, borderRadius: 6, padding: "5px 8px", cursor: "pointer" }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                                            </button>
                                        </div>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            {arrow(index > 0, () => onMove(index, "left"), "❮", `Move banner ${index + 1} left`)}
                                            {arrow(index < 3, () => onMove(index, "right"), "❯", `Move banner ${index + 1} right`)}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div role="button" tabIndex={0} onClick={() => onOpen(index + 1)} onKeyDown={(event) => { if (event.key === "Enter") onOpen(index + 1); }}
                                    style={{ width: "100%", flex: 1, border: `2px solid ${GOLD}`, borderRadius: 12, boxSizing: "border-box", display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", minWidth: 0, padding: 8, minHeight: 120 }}>
                                    <UploadIcon color={GOLD} />
                                    <span style={{ fontSize: 14, fontWeight: 600, color: GOLD, textAlign: "center" }}>Upload Banner</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {active && (
                <p style={{ fontSize: 14, color: TEXT, textAlign: "center", margin: "24px 0" }}>
                    After previewing your banners, click <b>Update</b> at bottom of page to upload them to our Chamber Perks App™ and website directories.
                </p>
            )}
            <div style={{ display: "flex", justifyContent: "center", marginTop: active ? 0 : 28 }}>
                <button type="button" className={classes.bannerButton} onClick={active ? onPreview : undefined} disabled={!active}
                    style={{ background: active ? BLACK : BORDER, cursor: active ? "pointer" : "default" }}>Preview Banners</button>
            </div>
        </CmpCard>
    );
};

export const CmpUpdateButton = ({ loading, disabled, classes, onClick }: { loading: boolean; disabled: boolean; classes: CmpClasses; onClick?: () => void }) => (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
        <button type={onClick ? "button" : "submit"} onClick={onClick} className={classes.update} disabled={disabled || loading}>
            {loading ? <CircularProgress size={18} style={{ color: "white" }} /> : "Update"}
        </button>
    </div>
);

export const CmpTextInput = (props: React.ComponentProps<typeof TextField> & { classes: CmpClasses }) => {
    const { classes, ...rest } = props;
    return <TextField variant="outlined" className={classes.companyInput} inputProps={{ autoComplete: "new-password" }} {...rest} />;
};
